import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModules } from '../../hooks/useModules';
import {
  BatchIcon,
  StillsIcon,
  EyewearIcon,
  JewelryIcon,
  HomeDecorIcon,
  EditorIcon,
  VideoIcon,
  MoodboardIcon,
  UserIcon,
  TrendForgeIcon,
} from '../icons/Icons';

const MODULE_SLUG_BY_CARD_ID: Record<string, string> = {
  lookbook: 'lookbook',
  'stills-vestuario': 'stills_vestuario',
  'stills-produtos': 'stills_produtos',
  eyewear: 'eyewear',
  jewelry: 'jewelry',
  homedecor: 'homedecor',
  editor: 'editor',
  veo: 'veo_studio',
  canvas: 'workflow',
  models: 'my_models',
  trendforge: 'trendforge',
};

interface AppCard {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  gradient: string;
  route: string;
  badge?: string;
  image?: string;
  video?: string;
}

const apps: AppCard[] = [
  {
    id: 'lookbook',
    titleKey: 'home.apps.lookbook',
    descriptionKey: 'home.apps.lookbookDesc',
    icon: <BatchIcon />,
    gradient: 'from-snapfy-primary via-[#3d0080] to-slate-900',
    route: '/batch',
    image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&q=80&w=800',
    video: 'https://snapfy-output.s3.us-east-1.amazonaws.com/Lookbook.mp4',
  },
  {
    id: 'stills-vestuario',
    titleKey: 'home.apps.stillsVestuario',
    descriptionKey: 'home.apps.stillsVestuarioDesc',
    icon: <StillsIcon className="w-6 h-6" />,
    gradient: 'from-rose-600 via-pink-700 to-slate-900',
    route: '/stills/vestuario',
    image: 'https://snapfy-output.s3.us-east-1.amazonaws.com/snapfy-canvas-item-1773837655362-0.3683615287531605.png',
  },
  {
    id: 'stills-produtos',
    titleKey: 'home.apps.stillsProdutos',
    descriptionKey: 'home.apps.stillsProdutosDesc',
    icon: <StillsIcon className="w-6 h-6" />,
    gradient: 'from-amber-600 via-orange-700 to-slate-900',
    route: '/stills/produtos',
    image: 'https://snapfy-output.s3.us-east-1.amazonaws.com/snapfy-canvas-item-1773837655362-0.3683615287531605.png',
  },
  {
    id: 'eyewear',
    titleKey: 'home.apps.eyewear',
    descriptionKey: 'home.apps.eyewearDesc',
    icon: <EyewearIcon className="w-6 h-6" />,
    gradient: 'from-cyan-600 via-teal-700 to-slate-900',
    route: '/accessories/eyewear',
    image: 'https://snapfy-output.s3.us-east-1.amazonaws.com/eyewear-img_1773943389524.png',
  },
  {
    id: 'jewelry',
    titleKey: 'home.apps.jewelry',
    descriptionKey: 'home.apps.jewelryDesc',
    icon: <JewelryIcon className="w-6 h-6" />,
    gradient: 'from-yellow-500 via-amber-700 to-slate-900',
    route: '/accessories/jewelry',
    image: 'https://snapfy-output.s3.us-east-1.amazonaws.com/jewelry-img_1773841783845.png',
  },
  {
    id: 'homedecor',
    titleKey: 'home.apps.homeDecor',
    descriptionKey: 'home.apps.homeDecorDesc',
    icon: <HomeDecorIcon />,
    gradient: 'from-emerald-600 via-green-700 to-slate-900',
    route: '/homedecor',
    image: 'https://snapfy-output.s3.us-east-1.amazonaws.com/snapfy-canvas-item-1773841011485-0.030882311053485934.png',
  },
  {
    id: 'editor',
    titleKey: 'home.apps.editor',
    descriptionKey: 'home.apps.editorDesc',
    icon: <EditorIcon />,
    gradient: 'from-blue-600 via-[#3d0080] to-slate-900', // #3d0080 residual — no token equivalent, Phase 7
    route: '/editor',
    image: 'https://snapfy-output.s3.us-east-1.amazonaws.com/snapfy-canvas-item-1773413384184-0.7484106061554555.png',
  },
  {
    id: 'veo',
    titleKey: 'home.apps.videos',
    descriptionKey: 'home.apps.videosDesc',
    icon: <VideoIcon className="w-6 h-6" />,
    gradient: 'from-red-600 via-rose-700 to-slate-900',
    route: '/video',
    image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&q=80&w=800',
    video: 'https://snapfy-output.s3.us-east-1.amazonaws.com/modelo-movimento.mp4',
  },
  {
    id: 'canvas',
    titleKey: 'home.apps.canvas',
    descriptionKey: 'home.apps.canvasDesc',
    icon: <MoodboardIcon />,
    gradient: 'from-violet-600 via-[var(--snapfy-primary-hover)] to-slate-900',
    route: '/workflow',
    image: 'https://snapfy-output.s3.us-east-1.amazonaws.com/snapfy-workflow.png',
  },
  {
    id: 'models',
    titleKey: 'home.apps.models',
    descriptionKey: 'home.apps.modelsDesc',
    icon: <UserIcon className="w-6 h-6" />,
    gradient: 'from-sky-600 via-blue-700 to-slate-900',
    route: '/models',
    image: 'https://snapfy-output.s3.us-east-1.amazonaws.com/snapfy-canvas-item-1773840322259-0.12778735129902286.png',
  },
  {
    id: 'trendforge',
    titleKey: 'home.apps.trendforge',
    descriptionKey: 'home.apps.trendforgeDesc',
    icon: <TrendForgeIcon className="w-6 h-6" />,
    gradient: 'from-fuchsia-600 via-pink-700 to-slate-900',
    route: '#',
    badge: 'home.comingSoon',
    image: 'https://snapfy-output.s3.us-east-1.amazonaws.com/snapfy-canvas-item-1773943652604-0.32046103914878055.png',
  },
];

const HomeDashboard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { isModuleVisible } = useModules();

  const visibleApps = useMemo(
    () =>
      apps.filter((app) => {
        const slug = MODULE_SLUG_BY_CARD_ID[app.id];
        return slug ? isModuleVisible(slug) : true;
      }),
    [isModuleVisible]
  );

  return (
    <div className="mt-16 mb-16 px-4 md:px-0">
      <div className="mb-6 md:mb-8 text-center md:text-left">
        <h2 className="text-2xl md:text-3xl font-black tracking-tight text-snapfy-text-primary mb-2">
          {t('home.exploreApps')}
        </h2>
        <p className="text-snapfy-text-secondary text-base md:text-lg">
          {t('home.exploreAppsDesc')}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
        {visibleApps.map((app) => {
          const cardContent = (
            <div className="group relative block text-left rounded-3xl overflow-hidden border border-snapfy-border transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-snapfy-primary/20 hover:border-snapfy-primary/50 w-full">
              {/* Image/Video Background */}
              <div className="relative aspect-[4/5] w-full overflow-hidden">
                {app.video ? (
                  <video
                    src={app.video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : app.image ? (
                  <img
                    src={app.image}
                    alt={t(app.titleKey)}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${app.gradient} transition-transform duration-700 group-hover:scale-110`}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent z-10" />

                {/* Icon & Badge */}
                <div className="absolute top-4 left-4 z-20 flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 backdrop-blur-md rounded-xl text-white border border-white/30 shadow-lg">
                    {app.icon}
                  </div>
                  {app.badge && (
                    <span className="px-3 py-1 bg-snapfy-primary text-white text-xs font-bold rounded-full shadow-lg">
                      {t(app.badge)}
                    </span>
                  )}
                </div>

                {/* Content Overlay (Glassmorphism) */}
                <div className="absolute bottom-4 left-4 right-4 z-20 p-4 md:p-5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-xl flex flex-col transition-all duration-300 group-hover:bg-white/15">
                  <h3 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2 group-hover:text-snapfy-accent-soft transition-colors">
                    {t(app.titleKey)}
                  </h3>
                  <p className="text-xs md:text-sm text-white/80 leading-relaxed line-clamp-2">
                    {t(app.descriptionKey)}
                  </p>

                  <div className="mt-2 md:mt-3 flex items-center text-snapfy-accent-soft text-xs md:text-sm font-bold opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                    {t('home.accessApp')}
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          );

          if (app.route === '#') {
            return (
              <div key={app.id} className="cursor-not-allowed opacity-80">
                {cardContent}
              </div>
            );
          }

          return (
            <Link key={app.id} to={app.route}>
              {cardContent}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default HomeDashboard;
